/*
 * @Descripttion:
 * @version:
 * @Author: lxh3@meitu.com
 * @Date: 2025-05-24 16:00:20
 * @LastEditors: chenhe ch10@meitu.com
 * @LastEditTime: 2025-10-22 16:16:58
 */
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { isMobile } from '$/utils/index';
import { ImagePreviewProps } from '$/pages/RoboneoChatBoard/type';
import React from 'react';

/**
 * 惰性加载 hotKey scope 切换方法。
 * hotKey 顶层静态依赖整套 canvas store（fabric / whee-infinite-canvas），
 * 仅在预览开关时才需要，改为动态加载避免拖入首页首屏。
 * @returns {Promise<typeof import('$/pages/RoboneoCanvas/components/InfiniteCanvas/hotKey')>}
 */
function loadHotKeyScopes() {
  // eslint-disable-next-line import/no-cycle
  return import('$/pages/RoboneoCanvas/components/InfiniteCanvas/hotKey');
}

interface VideoInfo {
  url: string;
  watermarkUrl?: string;
  poster: string;
  watermarkPoster?: string;
}
interface TPreviewState extends ImagePreviewProps {
  isShowVideo: boolean;
  videoInfo: VideoInfo | null;
  isShowMediaPreview: boolean;
  openPreview: boolean;
  openMobilePreview: boolean;
  previewShowDownload: boolean;
  previewShowQuote: boolean;
  previewShowZoom: boolean;
  previewHeaderMenuSlots: React.ReactElement | null;
  /** 每次打开桌面/移动媒体预览递增，供全屏视频一次性续播逻辑重置 */
  mediaPreviewSessionId: number;
}
const previewSlice: () => TPreviewState = () => ({
  isShowVideo: false,
  videoInfo: null,
  isShowMediaPreview: false,
  openPreview: false,
  previewImageList: [],
  initialIndex: 0,
  openMobilePreview: false,
  previewShowDownload: true,
  previewShowQuote: false,
  previewShowZoom: true,
  previewHeaderMenuSlots: null,
  mediaPreviewSessionId: 0,
});
const usePreviewStore = create<TPreviewState>()(immer(previewSlice));
export default usePreviewStore;

/**
 * 删除图片 根据索引
 * @param index
 */
export const setIsShowVideo = (val: boolean, videoInfo?: VideoInfo) => {
  usePreviewStore.setState(() => {
    return {
      isShowVideo: val,
      videoInfo,
    };
  });
};

/**
 * 预览弹窗打开状态
 * @param index
 */
export const setisShowMediaPreview = (val: boolean) => {
  usePreviewStore.setState(() => ({ isShowMediaPreview: val }));
};

/**
 * 预览索引
 *
 * @export
 * @param {number} index
 */
export function setInitialIndex(index: number) {
  const previewImageList = usePreviewStore.getState().previewImageList ?? [];
  const maxIndex = Math.max(previewImageList.length - 1, 0);
  // 预览列表可能在关闭后立刻切换为输入区单图预览，迟到的滚动回调不能把旧索引写回新列表。
  const safeIndex = Number.isFinite(index)
    ? Math.min(Math.max(index, 0), maxIndex)
    : 0;
  usePreviewStore.setState(() => ({ initialIndex: safeIndex }));
}

/**
 * 开启预览
 *
 * @export
 * @param {ImagePreviewProps} {
 *   previewImageList,
 *   initialIndex = 0,
 * }
 */
export function openPreview({
  previewImageList,
  initialIndex = 0,
  previewShowDownload = true,
  previewShowQuote = false,
  previewShowZoom = true,
  previewHeaderMenuSlots = null,
}: ImagePreviewProps) {
  const prevSession = usePreviewStore.getState().mediaPreviewSessionId ?? 0;
  const safePreviewImageList = (previewImageList ?? []).filter(Boolean);
  const maxIndex = Math.max(safePreviewImageList.length - 1, 0);
  // 打开新预览时先归一索引，避免上一轮预览的 current 落到新列表范围外。
  const safeInitialIndex = Number.isFinite(initialIndex)
    ? Math.min(Math.max(initialIndex, 0), maxIndex)
    : 0;
  const shouldOpenPreview = safePreviewImageList.length > 0;
  usePreviewStore.setState(() => {
    return {
      previewImageList: safePreviewImageList,
      initialIndex: safeInitialIndex,
      openPreview: shouldOpenPreview && !isMobile(),
      openMobilePreview: shouldOpenPreview && isMobile(),
      isShowMediaPreview: shouldOpenPreview,
      previewShowDownload: previewShowDownload ?? true,
      previewShowQuote: previewShowQuote ?? false,
      previewShowZoom: previewShowZoom ?? true,
      previewHeaderMenuSlots,
      mediaPreviewSessionId: prevSession + 1,
    };
  });
  loadHotKeyScopes().then(({ setPreviewScope }) => setPreviewScope());
}

/**
 * 关闭预览
 *
 * @export
 */
export function closePreview() {
  // 关闭预览时重置快捷键 scope（canvas 相关依赖动态加载，避免拖入首页首屏）
  Promise.all([
    loadHotKeyScopes(),
    import('$/store/useAIEditImageStore'),
  ]).then(([{ setEditImageScope, setCanvasScope }, aiEditImageModule]) => {
    if (aiEditImageModule.default.getState().editImageMode) {
      setEditImageScope();
    } else {
      setCanvasScope();
    }
  });
  usePreviewStore.setState(() => {
    return {
      openPreview: false,
      openMobilePreview: false,
      isShowMediaPreview: false,
      previewShowQuote: false,
      previewShowZoom: true,
      previewHeaderMenuSlots: null,
    };
  });
}
